'use client';

import React, { useCallback, useMemo, useState } from 'react';

import Button from '@/components/button/Button';
import CardWrapper from '@/components/CardWrapper.tsx';
import VideoPlayerModal from '@/components/video/VideoPlayerModal';

import { isYoutubeVideoUrl } from '@/constants/youtube-constants';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { IconPlayButton56 } from '@cineamo/ui/src/icons/ic-playButton';
import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type HomePageCardProps = {
    title?: string;
    text?: string;
    buttonText?: string;
    url?: string;
    cardBackground?: string;
    cardBackgroundAlt?: string;
    video?: string;
};

const HomePageCard: React.FC<HomePageCardProps> = ({
    text,
    title,
    buttonText,
    url,
    cardBackground,
    cardBackgroundAlt,
    video
}) => {
    const domainConfiguration = useDomainConfiguration();
    const { track } = useAnalytics();
    const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);

    // A YouTube url is treated as a video: the card opens the player modal instead of navigating
    const urlIsYoutube = isYoutubeVideoUrl(url);
    const videoUrl = video || (urlIsYoutube ? url : undefined);
    const linkUrl = urlIsYoutube ? undefined : url;

    const openPlayerModal = useCallback(() => {
        setIsPlayerModalOpen(true);
        track('Card Video Played', {
            cardVideo: videoUrl ?? ''
        });
    }, [videoUrl, track]);

    const card = useMemo(
        () => (
            <CardWrapper
                className={cn(
                    'relative w-[325px] h-[356px] flex-none flex flex-col',
                    'rounded-8 border-2 border-[var(--surface-color)] overflow-hidden'
                )}>
                {cardBackground && (
                    <div className="relative w-full h-[185px] rounded-4 overflow-hidden">
                        <ImageWithTransition
                            className="w-full h-[185px] object-cover"
                            src={cardBackground}
                            width={400}
                            height={300}
                            alt={cardBackgroundAlt || 'Card Image'}
                        />
                        {videoUrl && (
                            <div
                                className={cn(
                                    'absolute top-0 left-0 w-full h-full',
                                    'flex flex-auto items-center justify-center'
                                )}>
                                <IconPlayButton56
                                    className="pointer-events-auto clickable-element"
                                    foregroundcolor="white"
                                    fill="black"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        event.preventDefault();
                                        openPlayerModal();
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}

                <div className="relative w-full h-0 flex-auto flex flex-col">
                    <div className="flex flex-col h-full space-y-4 justify-between p-10">
                        {title && (
                            <span
                                className={cn(
                                    'relative text-body-bold-16 whitespace-wrap text-[var(--on-background-color)] leading-[28px]'
                                )}>
                                {title}
                            </span>
                        )}
                        {text && (
                            <span
                                className={cn(
                                    'relative whitespace-pre-wrap text-[rgba(var(--on-background-color-rgb),0.7)]',
                                    'text-body-12 leading-[20px]',
                                    'overflow-auto no-scrollbar'
                                )}>
                                {text.trim()}
                            </span>
                        )}
                        <span className="grow" />
                        {buttonText && (
                            <Button
                                className={cn('relative w-full', {
                                    'pointer-events-none': !!linkUrl || !!videoUrl
                                })}
                                css={{
                                    backgroundColor: domainConfiguration.style.primaryColor,
                                    color: domainConfiguration.style.onPrimaryColor
                                }}
                                labelClassName="text-body-bold-14"
                                onClick={(e) => e.preventDefault()}>
                                {buttonText}
                            </Button>
                        )}
                    </div>
                </div>
            </CardWrapper>
        ),
        [
            linkUrl,
            videoUrl,
            text,
            buttonText,
            cardBackground,
            cardBackgroundAlt,
            title,
            domainConfiguration,
            openPlayerModal
        ]
    );

    // const target = link.includes(domainConfiguration.domain) ? '_self' : '_blank';
    const target = '_self';

    return (
        <>
            {videoUrl && (
                <VideoPlayerModal
                    url={videoUrl}
                    isOpen={isPlayerModalOpen}
                    setIsOpen={setIsPlayerModalOpen}
                />
            )}
            {linkUrl ? (
                <Link
                    href={linkUrl}
                    target={target}
                    rel="noreferrer"
                    onClick={() => track('Home Card Clicked', { title: title ?? '' })}>
                    {card}
                </Link>
            ) : videoUrl ? (
                <div
                    className="cursor-pointer"
                    onClick={openPlayerModal}>
                    {card}
                </div>
            ) : (
                card
            )}
        </>
    );
};

export default HomePageCard;
